import { queryOptions } from '@tanstack/react-query';
import { GlobalPermissionType } from '@wavingroup/iot-core/gen/ts/wavin/kleos/v1/global_permission_pb';
import { listUsers } from '~/services/api/fetchers/listUsers.api';
import { listUsersByNames } from '~/services/api/fetchers/listUsersByNames.api';
import { listGlobalPermissions } from '~/services/api/fetchers/globalPermissions.api';
import { ENABLE_KLEOS_ROLES } from '~/services/environment/environment';
import { IgniteUserQueryKey } from '~/services/queryKeys';
import { UsersListModel } from '~/shared/models/usersList/UsersListModel';
import { isAssignableKleosPermission } from '~/shared/models/role/kleosRoleMapping';

function groupAssignablePermissions(
  grants: Awaited<ReturnType<typeof listGlobalPermissions>>,
) {
  const globalPermissionsBySubject = new Map<string, GlobalPermissionType[]>();

  grants.forEach((grant) => {
    const assignable = grant.permissions.filter(isAssignableKleosPermission);
    if (assignable.length === 0) {
      return;
    }

    const existing = globalPermissionsBySubject.get(grant.subjectName) ?? [];
    globalPermissionsBySubject.set(grant.subjectName, [
      ...existing,
      ...assignable,
    ]);
  });

  return globalPermissionsBySubject;
}

const USER_SUBJECT_PREFIX = 'users/';

export const listUsersQuery = queryOptions({
  queryKey: [IgniteUserQueryKey.LIST_USERS],
  queryFn: async () => {
    if (!ENABLE_KLEOS_ROLES) {
      const response = await listUsers();
      return new UsersListModel(response.users);
    }

    const grants = await listGlobalPermissions();
    const globalPermissionsBySubject = groupAssignablePermissions(grants);
    const userNames = [...globalPermissionsBySubject.keys()].filter(
      (subjectName) => subjectName.startsWith(USER_SUBJECT_PREFIX),
    );

    const users = await listUsersByNames(userNames);
    return new UsersListModel(users, globalPermissionsBySubject);
  },
});
